import {
  ClinicalTrialReportFromApi,
  ClinicalTrialReportsFetchRequestAction,
  ClinicalTrialReportsFetchSuccessAction,
  ClinicalTrialReportsState,
  ClinicalTrialReportUpdateStatusRequestAction,
  ClinicalTrialReportUrlFetchRequestAction,
  ClinicalTrialReportUrlFetchSuccessAction,
  FetchDictionaryRequestAction,
  FetchDictionarySuccessAction,
} from '@restore/clinicalTrialReports/clinicalTrialReports.types';
import {
  createSelector,
  createSliceHook,
  makeActionCreator,
} from '@restore/helpers';
import { createSlice } from '@reduxjs/toolkit';

const initialState: ClinicalTrialReportsState = {
  data: {
    reports: [],
    totalElements: 0,
  },
  dictionaries: {
    clinicalTrialId: '',
  },
  PDFReport: null,
  search: '',
  loading: false,
  loadingProcedure: false,
  loadingDictionaries: false,
  loadingFile: false,
  error: null,
};

const { act, actReq, actResolve, actReject } =
  makeActionCreator<ClinicalTrialReportsState>();

export const clinicalTrialReportsSlice = createSlice({
  name: 'clinicalTrialReports',
  initialState,
  reducers: {
    fetchClinicalTrialReportsRequest:
      actReq<ClinicalTrialReportsFetchRequestAction['payload']>('loading'),

    fetchClinicalTrialReportsSuccess: actResolve<
      ClinicalTrialReportsFetchSuccessAction['payload']
    >('data', 'loading'),

    fetchClinicalTrialReportsFailure: actReject('error', 'loading'),

    fetchDictionariesRequest: actReq<FetchDictionaryRequestAction['payload']>(
      'loadingDictionaries'
    ),

    fetchDictionariesSuccess: actResolve<
      FetchDictionarySuccessAction['payload']
    >('dictionaries', 'loadingDictionaries'),

    fetchDictionariesFailure: actReject(
      'dictionariesError',
      'loadingDictionaries'
    ),

    updateReportStatusRequest:
      actReq<ClinicalTrialReportUpdateStatusRequestAction['payload']>(
        'loadingProcedure'
      ),
    updateReportStatusSuccess: actResolve<ClinicalTrialReportFromApi>(
      null,
      'loadingProcedure'
    ),
    updateReportStatusFailure: actReject('error', 'loadingProcedure'),

    fetchReportUrlRequest:
      actReq<ClinicalTrialReportUrlFetchRequestAction['payload']>(
        'loadingFile'
      ),
    fetchReportUrlSuccess: act<
      ClinicalTrialReportUrlFetchSuccessAction['payload']
    >(PDFReport => ({ PDFReport, fileError: null, loadingFile: false })),

    fetchReportUrlFailure: actReject('fileError', 'loadingFile'),

    removeReportUrl: state => {
      state.PDFReport = null;
    },

    setSearch: actResolve<string>('search', null),

    clear: state => {
      state.data = initialState.data;
      state.search = initialState.search;
      state.loading = initialState.loading;
      state.loadingDictionaries = initialState.loadingDictionaries;
      state.loadingFile = initialState.loadingFile;
      state.error = initialState.error;
    },
  },
});

export const {
  fetchClinicalTrialReportsRequest,
  fetchClinicalTrialReportsSuccess,
  fetchClinicalTrialReportsFailure,
  fetchDictionariesRequest,
  clear,
  fetchDictionariesFailure,
  fetchDictionariesSuccess,
  fetchReportUrlFailure,
  fetchReportUrlRequest,
  fetchReportUrlSuccess,
  setSearch,
  updateReportStatusFailure,
  updateReportStatusRequest,
  updateReportStatusSuccess,
} = clinicalTrialReportsSlice.actions;

export const actions = {
  ...clinicalTrialReportsSlice.actions,
};

export const useClinicalTrialReportsSlice = createSliceHook(
  clinicalTrialReportsSlice.name,
  actions
);
export const selectClinicalTrialReportsState = createSelector(
  clinicalTrialReportsSlice.name
);
